import { Clock, Calendar, FileText, AlertCircle, ChevronRight, TrendingUp, CheckCircle2, Info, Users, ClipboardCheck, Heart, Timer, Smile, Star } from 'lucide-react';
import { Button } from './core/Button';
import { Card, CardHeader, CardContent } from './core/Card';
import { useAuth } from '../graphql/hooks/useAuth';
import { UserDashboard } from './UserDashboard';

export function DashboardView() {
  const { userType, userName } = useAuth();
  const isAdminView = userType === 'admin' || userType === 'superadmin';
  
  // If user view (not admin), show the new human-centered dashboard
  if (!isAdminView) {
    return <UserDashboard />;
  }
  
  // Current date: Thursday, January 8, 2026
  const currentDate = new Date(2026, 0, 8);

  // Get first name only for personal greeting
  const getFirstName = (fullName: string) => {
    return fullName.split(' ')[0] || fullName;
  };

  // User data (derived from approved entries and requests)
  const userData = {
    name: userName || 'Holger',
    firstName: getFirstName(userName || 'Holger'),
    overtime: 16.5, // Zeitguthaben (hours)
    vacationDays: 20, // Verbleibender Urlaub
    totalVacationDays: 30, // Total available per year
    pendingRequests: 1, // Offene Anträge
    missingEntries: 2, // Missing time entries from previous days
  };

  // Admin-specific data
  const adminData = {
    pendingRequestsTotal: 12, // Total pending requests from all employees
    teamOvertimeTotal: 245.5, // Total team overtime hours
    teamVacationAverage: 18, // Average remaining vacation days
    activeEmployeesToday: 47, // Employees who have clocked in today
    totalEmployees: 52, // Total number of employees
    criticalRequests: [
      {
        id: 'REQ-089',
        employee: 'Müller, Thomas',
        type: 'Urlaub',
        startDate: '2026-01-13',
        endDate: '2026-01-17',
        days: 5,
        urgent: true,
        submittedDate: '2026-01-05',
      },
      {
        id: 'REQ-091',
        employee: 'Schmidt, Anna',
        type: 'Krankmeldung',
        startDate: '2026-01-08',
        endDate: '2026-01-10',
        days: 3,
        urgent: false,
        submittedDate: '2026-01-08',
      },
      {
        id: 'REQ-087',
        employee: 'Weber, Klaus',
        type: 'Zeitkorrektur',
        startDate: '2026-01-06',
        endDate: '2026-01-06',
        days: 1,
        urgent: false,
        submittedDate: '2026-01-07',
      },
    ],
    missingEntriesTeam: [
      {
        employee: 'Meyer, Lisa',
        count: 3,
        lastEntry: '2026-01-03',
      },
      {
        employee: 'Fischer, Marc',
        count: 2,
        lastEntry: '2026-01-05',
      },
    ],
  };

  // Missing time entries
  const missingEntries = [
    {
      date: '2026-01-06',
      dayName: 'Montag',
      displayDate: '06.01.2026',
    },
    {
      date: '2026-01-07',
      dayName: 'Dienstag',
      displayDate: '07.01.2026',
    },
  ];

  // Pending requests
  const pendingRequests = [
    {
      id: '1',
      type: 'Urlaub',
      startDate: '2026-01-20',
      endDate: '2026-01-24',
      days: 5,
      description: 'Winterurlaub',
      submittedDate: '2026-01-08',
    },
  ];

  const getGreeting = () => {
    const hour = currentDate.getHours();
    if (hour < 12) return 'Guten Morgen';
    if (hour < 18) return 'Guten Tag';
    return 'Guten Abend';
  };

  const getFormattedDate = () => {
    const days = ['Sonntag', 'Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag'];
    const months = [
      'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
      'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
    ];
    
    const dayName = days[currentDate.getDay()];
    const day = currentDate.getDate();
    const month = months[currentDate.getMonth()];
    const year = currentDate.getFullYear();
    
    return `${dayName}, ${day}. ${month} ${year}`;
  };

  // Determine primary CTA
  const hasMissingEntries = userData.missingEntries > 0;
  const hasPendingRequests = userData.pendingRequests > 0;

  const primaryCTA = hasMissingEntries
    ? { label: 'Zeiterfassung öffnen', action: 'zeiterfassung' }
    : hasPendingRequests
    ? { label: 'Meine Anträge ansehen', action: 'anträge' }
    : { label: 'Zeiterfassung öffnen', action: 'zeiterfassung' };

  const handleCTAClick = () => {
    // Dispatch navigation event
    const event = new CustomEvent('navigate', {
      detail: { page: primaryCTA.action }
    });
    window.dispatchEvent(event);
  };

  const handleRequestsClick = () => {
    const event = new CustomEvent('navigate', {
      detail: { page: 'anträge' }
    });
    window.dispatchEvent(event);
  };

  const handleTimeTrackingClick = () => {
    const event = new CustomEvent('navigate', {
      detail: { page: 'zeiterfassung' }
    });
    window.dispatchEvent(event);
  };

  return (
    <div className="bg-[#f5f5f5] min-h-screen p-4 md:p-8">
      <div className="max-w-[1400px] mx-auto">
        {/* Greeting Section */}
        <div className="mb-6 md:mb-8">
          <h1 className="text-[#1c1c1c] text-[28px] md:text-[32px] font-medium leading-tight mb-1">
            {getGreeting()}, {userData.firstName}
          </h1>
          <p className="text-[#6b6b6b] text-[16px] md:text-[18px]">{getFormattedDate()}</p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-6 md:mb-8">
          {isAdminView ? (
            <>
              {/* Admin KPI: Offene Anträge (Total) - Clickable */}
              <button
                onClick={() => {
                  const event = new CustomEvent('navigate', { detail: { page: 'anträge' } });
                  window.dispatchEvent(event);
                }}
                className="text-left transition-all hover:scale-[1.02]"
              >
                <Card>
                  <CardContent>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <ClipboardCheck className="size-5 text-[#6b6b6b]" />
                          <p className="text-[#6b6b6b] text-[14px] font-medium">Offene Anträge</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                            {adminData.pendingRequestsTotal}
                          </p>
                        </div>
                        <p className="text-[#6b6b6b] text-[13px] mt-1">
                          Warten auf Entscheidung
                        </p>
                      </div>
                      <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium ${
                        adminData.pendingRequestsTotal > 0 
                          ? 'bg-orange-50 text-orange-700' 
                          : 'bg-green-50 text-green-700'
                      }`}>
                        {adminData.pendingRequestsTotal > 0 ? (
                          <>
                            <AlertCircle className="size-3" />
                            Aktion nötig
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="size-3" />
                            Alle erledigt
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>

              {/* Admin KPI: Zeitguthaben Team */}
              <Card>
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="size-5 text-[#6b6b6b]" />
                        <p className="text-[#6b6b6b] text-[14px] font-medium">Zeitguthaben Team</p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                          +{adminData.teamOvertimeTotal}
                        </p>
                        <span className="text-[#6b6b6b] text-[18px]">h</span>
                      </div>
                      <p className="text-[#6b6b6b] text-[13px] mt-1">Gesamt Team-Überstunden</p>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-green-50 text-green-700">
                      <TrendingUp className="size-3" />
                      Positiv
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Admin KPI: Verbleibender Urlaub (Durchschnitt) */}
              <Card>
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="size-5 text-[#6b6b6b]" />
                        <p className="text-[#6b6b6b] text-[14px] font-medium">Ø Urlaub Team</p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                          {adminData.teamVacationAverage}
                        </p>
                        <span className="text-[#6b6b6b] text-[18px]">Tage</span>
                      </div>
                      <p className="text-[#6b6b6b] text-[13px] mt-1">
                        Durchschnittlich verbleibend
                      </p>
                    </div>
                    <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-blue-50 text-blue-700">
                      <Info className="size-3" />
                      Info
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Admin KPI: Aktive Mitarbeiter heute - Clickable */}
              <button
                onClick={() => {
                  const event = new CustomEvent('navigate', { detail: { page: 'auswertung' } });
                  window.dispatchEvent(event);
                }}
                className="text-left transition-all hover:scale-[1.02]"
              >
                <Card>
                  <CardContent>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Users className="size-5 text-[#6b6b6b]" />
                          <p className="text-[#6b6b6b] text-[14px] font-medium">Aktive Mitarbeiter</p>
                        </div>
                        <div className="flex items-baseline gap-2">
                          <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                            {adminData.activeEmployeesToday}
                          </p>
                          <span className="text-[#6b6b6b] text-[18px]">/ {adminData.totalEmployees}</span>
                        </div>
                        <p className="text-[#6b6b6b] text-[13px] mt-1">
                          Heute eingecheckt
                        </p>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-green-50 text-green-700">
                        <CheckCircle2 className="size-3" />
                        {Math.round((adminData.activeEmployeesToday / adminData.totalEmployees) * 100)}%
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </button>
            </>
          ) : (
            <>
              {/* User KPI: Zeitguthaben */}
              <Card>
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="size-5 text-[#6b6b6b]" />
                        <p className="text-[#6b6b6b] text-[14px] font-medium">Zeitguthaben</p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                          {userData.overtime > 0 ? '+' : ''}{userData.overtime}
                        </p>
                        <span className="text-[#6b6b6b] text-[18px]">h</span>
                      </div>
                      <p className="text-[#6b6b6b] text-[13px] mt-1">Überstunden aufgebaut</p>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium ${
                      userData.overtime > 0 ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-600'
                    }`}>
                      <TrendingUp className="size-3" />
                      {userData.overtime > 0 ? 'Positiv' : 'Neutral'}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User KPI: Verbleibender Urlaub */}
              <Card>
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <Calendar className="size-5 text-[#6b6b6b]" />
                        <p className="text-[#6b6b6b] text-[14px] font-medium">Verbleibender Urlaub</p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                          {userData.vacationDays}
                        </p>
                        <span className="text-[#6b6b6b] text-[18px]">Tage</span>
                      </div>
                      <p className="text-[#6b6b6b] text-[13px] mt-1">
                        von {userData.totalVacationDays} Tagen
                      </p>
                    </div>
                    <div className="mt-3">
                      {/* Progress bar */}
                      <div className="w-[60px] h-[60px] relative">
                        <svg className="transform -rotate-90" width="60" height="60">
                          <circle
                            cx="30"
                            cy="30"
                            r="24"
                            fill="none"
                            stroke="#e9e9e9"
                            strokeWidth="4"
                          />
                          <circle
                            cx="30"
                            cy="30"
                            r="24"
                            fill="none"
                            stroke="#0084c7"
                            strokeWidth="4"
                            strokeDasharray={`${(userData.vacationDays / userData.totalVacationDays) * 150.8} 150.8`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-[#1c1c1c] text-[14px] font-medium">
                            {Math.round((userData.vacationDays / userData.totalVacationDays) * 100)}%
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* User KPI: Offene Anträge */}
              <Card>
                <CardContent>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <FileText className="size-5 text-[#6b6b6b]" />
                        <p className="text-[#6b6b6b] text-[14px] font-medium">Offene Anträge</p>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <p className="text-[#1c1c1c] text-[32px] md:text-[36px] font-medium">
                          {userData.pendingRequests}
                        </p>
                      </div>
                      <p className="text-[#6b6b6b] text-[13px] mt-1">
                        {userData.pendingRequests === 0 
                          ? 'Keine offenen Anträge' 
                          : userData.pendingRequests === 1
                          ? 'Wartet auf Genehmigung'
                          : 'Warten auf Genehmigung'
                        }
                      </p>
                    </div>
                    <div className={`flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium ${
                      userData.pendingRequests > 0 
                        ? 'bg-orange-50 text-orange-700' 
                        : 'bg-green-50 text-green-700'
                    }`}>
                      {userData.pendingRequests > 0 ? (
                        <>
                          <Clock className="size-3" />
                          Offen
                        </>
                      ) : (
                        <>
                          <CheckCircle2 className="size-3" />
                          Alle erledigt
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          {/* Left Column - Notifications and Actions */}
          <div className="lg:col-span-2 space-y-4 md:space-y-6">
            {isAdminView ? (
              <>
                {/* Admin: Handlungsbedarf - Pending Requests */}
                {adminData.pendingRequestsTotal > 0 && (
                  <Card>
                    <CardHeader
                      title="Handlungsbedarf"
                      subtitle="Anträge, die Ihre Entscheidung benötigen"
                      borderBottom
                    />
                    <CardContent>
                      <div className="space-y-3">
                        {adminData.criticalRequests.map((request, index) => (
                          <div 
                            key={index}
                            className={`rounded-[10px] p-4 border-2 ${
                              request.urgent 
                                ? 'bg-orange-50 border-orange-300' 
                                : 'bg-blue-50 border-blue-200'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`size-10 rounded-full flex items-center justify-center shrink-0 ${
                                request.urgent 
                                  ? 'bg-orange-100' 
                                  : 'bg-blue-100'
                              }`}>
                                <ClipboardCheck className={`size-5 ${
                                  request.urgent 
                                    ? 'text-orange-700' 
                                    : 'text-blue-700'
                                }`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-700">
                                    {request.type}
                                  </span>
                                  <span className="text-[#6b6b6b] text-[13px]">
                                    {request.id}
                                  </span>
                                  {request.urgent && (
                                    <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-red-100 text-red-700">
                                      Dringend
                                    </span>
                                  )}
                                </div>
                                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                                  {request.employee}
                                </h3>
                                <p className="text-[#6b6b6b] text-[14px] mb-2">
                                  {new Date(request.startDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - {new Date(request.endDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })} ({request.days} {request.days === 1 ? 'Tag' : 'Tage'})
                                </p>
                                <p className="text-[#9b9b9b] text-[12px] mb-3">
                                  Eingereicht am {new Date(request.submittedDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                </p>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleRequestsClick}
                                  icon={<ClipboardCheck className="size-4" />}
                                >
                                  Jetzt prüfen
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Admin: Fehlende Zeiteinträge im Team */}
                {adminData.missingEntriesTeam.length > 0 && (
                  <Card>
                    <CardHeader
                      title="Fehlende Zeiteinträge"
                      subtitle="Mitarbeiter mit unvollständiger Zeiterfassung"
                      borderBottom
                    />
                    <CardContent>
                      <div className="space-y-3">
                        {adminData.missingEntriesTeam.map((entry, index) => (
                          <div 
                            key={index}
                            className="bg-amber-50 border border-amber-200 rounded-[10px] p-4"
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-start gap-3 flex-1">
                                <div className="size-10 bg-amber-100 rounded-full flex items-center justify-center shrink-0">
                                  <AlertCircle className="size-5 text-amber-700" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                                    {entry.employee}
                                  </h3>
                                  <p className="text-[#6b6b6b] text-[14px] mb-1">
                                    {entry.count} {entry.count === 1 ? 'fehlender Eintrag' : 'fehlende Einträge'}
                                  </p>
                                  <p className="text-[#9b9b9b] text-[12px]">
                                    Letzter Eintrag: {new Date(entry.lastEntry).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-1 px-2 py-1 rounded-md text-[12px] font-medium bg-amber-100 text-amber-700 h-fit">
                                <AlertCircle className="size-3" />
                                Nachfragen
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Admin: All Clear */}
                {adminData.pendingRequestsTotal === 0 && adminData.missingEntriesTeam.length === 0 && (
                  <Card>
                    <CardContent>
                      <div className="bg-green-50 border border-green-200 rounded-[10px] p-6 text-center">
                        <div className="size-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="size-8 text-green-700" />
                        </div>
                        <h3 className="text-[#1c1c1c] text-[18px] font-medium mb-2">
                          Keine offenen Aufgaben
                        </h3>
                        <p className="text-[#6b6b6b] text-[14px] max-w-md mx-auto">
                          Alle Anträge wurden bearbeitet und es liegen keine kritischen Zeiterfassungen vor.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Admin: Team Info */}
                <Card>
                  <CardHeader
                    title="Team-Übersicht"
                    subtitle="Wichtige Kennzahlen"
                    borderBottom
                  />
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-[8px]">
                        <Users className="size-5 text-[#0084c7] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#1c1c1c] text-[14px] font-medium mb-1">
                            Aktive Mitarbeiter heute
                          </h4>
                          <p className="text-[#6b6b6b] text-[13px]">
                            <strong>{adminData.activeEmployeesToday} von {adminData.totalEmployees} Mitarbeitern</strong> haben sich heute eingecheckt. Das entspricht einer Anwesenheitsquote von <strong>{Math.round((adminData.activeEmployeesToday / adminData.totalEmployees) * 100)}%</strong>.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-[8px]">
                        <Clock className="size-5 text-[#0084c7] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#1c1c1c] text-[14px] font-medium mb-1">
                            Team-Zeitguthaben
                          </h4>
                          <p className="text-[#6b6b6b] text-[13px]">
                            Das Team hat insgesamt <strong>{adminData.teamOvertimeTotal} Überstunden</strong> aufgebaut. Diese können als Freizeitausgleich genommen werden.
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-[8px]">
                        <Calendar className="size-5 text-[#0084c7] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#1c1c1c] text-[14px] font-medium mb-1">
                            Urlaubsplanung
                          </h4>
                          <p className="text-[#6b6b6b] text-[13px]">
                            Im Durchschnitt haben Mitarbeiter noch <strong>{adminData.teamVacationAverage} Urlaubstage</strong> für dieses Jahr verfügbar. Planen Sie Abwesenheiten frühzeitig.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            ) : (
              <>
                {/* User View: Original Action Required Card */}
                {(hasMissingEntries || hasPendingRequests) && (
                  <Card>
                    <CardHeader
                      title="Handlungsbedarf"
                      subtitle="Bitte beachten Sie folgende offene Punkte"
                      borderBottom
                    />
                    <CardContent>
                      <div className="space-y-3">
                        {/* Missing Time Entries */}
                        {hasMissingEntries && (
                          <div className="bg-blue-50 border border-blue-200 rounded-[10px] p-4">
                            <div className="flex items-start gap-3">
                              <div className="size-10 bg-blue-100 rounded-full flex items-center justify-center shrink-0">
                                <AlertCircle className="size-5 text-blue-700" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                                  {userData.missingEntries} fehlende Zeiteinträge
                                </h3>
                                <p className="text-[#6b6b6b] text-[14px] mb-3">
                                  Bitte erfassen Sie Ihre Arbeitszeit für folgende Tage:
                                </p>
                                <div className="space-y-2 mb-4">
                                  {missingEntries.map((entry, index) => (
                                    <div 
                                      key={index}
                                      className="flex items-center gap-2 text-[14px]"
                                    >
                                      <Calendar className="size-4 text-blue-600" />
                                      <span className="text-[#1c1c1c] font-medium">{entry.dayName}</span>
                                      <span className="text-[#6b6b6b]">{entry.displayDate}</span>
                                    </div>
                                  ))}
                                </div>
                                <Button
                                  variant="primary"
                                  size="sm"
                                  onClick={handleTimeTrackingClick}
                                  icon={<Clock className="size-4" />}
                                >
                                  Jetzt erfassen
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Pending Requests */}
                        {hasPendingRequests && (
                          <div className="bg-orange-50 border border-orange-200 rounded-[10px] p-4">
                            <div className="flex items-start gap-3">
                              <div className="size-10 bg-orange-100 rounded-full flex items-center justify-center shrink-0">
                                <Clock className="size-5 text-orange-700" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 className="text-[#1c1c1c] text-[16px] font-medium mb-1">
                                  {userData.pendingRequests} {userData.pendingRequests === 1 ? 'Antrag wartet' : 'Anträge warten'} auf Genehmigung
                                </h3>
                                <p className="text-[#6b6b6b] text-[14px] mb-3">
                                  Ihre Anträge werden überprüft. Sie werden benachrichtigt, sobald eine Entscheidung getroffen wurde.
                                </p>
                                <div className="space-y-2 mb-4">
                                  {pendingRequests.map((request, index) => (
                                    <div 
                                      key={index}
                                      className="bg-white rounded-[8px] p-3 border border-orange-200"
                                    >
                                      <div className="flex items-center gap-2 mb-1">
                                        <span className="inline-flex px-2 py-0.5 rounded text-[11px] font-medium bg-blue-100 text-blue-700">
                                          {request.type}
                                        </span>
                                        <span className="text-[#6b6b6b] text-[13px]">
                                          {request.days} {request.days === 1 ? 'Tag' : 'Tage'}
                                        </span>
                                      </div>
                                      <p className="text-[#1c1c1c] text-[14px]">
                                        {new Date(request.startDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} - {new Date(request.endDate).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                                      </p>
                                      {request.description && (
                                        <p className="text-[#6b6b6b] text-[13px] mt-1">
                                          {request.description}
                                        </p>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={handleRequestsClick}
                                  icon={<FileText className="size-4" />}
                                >
                                  Zu meinen Anträgen
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* User View: All Clear Message */}
                {!hasMissingEntries && !hasPendingRequests && (
                  <Card>
                    <CardContent>
                      <div className="bg-green-50 border border-green-200 rounded-[10px] p-6 text-center">
                        <div className="size-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <CheckCircle2 className="size-8 text-green-700" />
                        </div>
                        <h3 className="text-[#1c1c1c] text-[18px] font-medium mb-2">
                          Alles erledigt!
                        </h3>
                        <p className="text-[#6b6b6b] text-[14px] max-w-md mx-auto">
                          Sie haben keine offenen Zeiteinträge und alle Ihre Anträge wurden bearbeitet.
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* User View: Quick Info Card */}
                <Card>
                  <CardHeader
                    title="Wissenswertes"
                    borderBottom
                  />
                  <CardContent>
                    <div className="space-y-4">
                      <div className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-[8px]">
                        <Info className="size-5 text-[#0084c7] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#1c1c1c] text-[14px] font-medium mb-1">
                            Überstunden abbummeln
                          </h4>
                          <p className="text-[#6b6b6b] text-[13px]">
                            Sie haben aktuell <strong>{userData.overtime}h Zeitguthaben</strong>. Diese können Sie als bezahlte Freizeit nehmen, indem Sie einen Antrag vom Typ "Überstunden abfeiern" stellen.
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-[8px]">
                        <Info className="size-5 text-[#0084c7] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#1c1c1c] text-[14px] font-medium mb-1">
                            Urlaubsplanung
                          </h4>
                          <p className="text-[#6b6b6b] text-[13px]">
                            Von Ihren <strong>{userData.totalVacationDays} Urlaubstagen</strong> in diesem Jahr sind noch <strong>{userData.vacationDays} Tage verfügbar</strong>. Planen Sie Ihren Urlaub rechtzeitig!
                          </p>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 p-3 bg-[#f5f5f5] rounded-[8px]">
                        <Info className="size-5 text-[#0084c7] shrink-0 mt-0.5" />
                        <div>
                          <h4 className="text-[#1c1c1c] text-[14px] font-medium mb-1">
                            Zeiterfassung
                          </h4>
                          <p className="text-[#6b6b6b] text-[13px]">
                            Erfassen Sie Ihre Arbeitszeit täglich, um eine genaue Übersicht über Ihre Stunden zu behalten. Fehlende Einträge werden hier automatisch angezeigt.
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>

          {/* Right Sidebar - Primary CTA and Quick Stats */}
          <div className="space-y-4 md:space-y-6">
            {/* Primary CTA Card */}
            <Card>
              <CardHeader
                title="Schnellzugriff"
                borderBottom
              />
              <CardContent>
                <Button
                  variant="primary"
                  fullWidth
                  size="lg"
                  onClick={handleCTAClick}
                  icon={primaryCTA.action === 'zeiterfassung' ? <Clock className="size-5" /> : <FileText className="size-5" />}
                  iconPosition="left"
                >
                  {primaryCTA.label}
                </Button>
                
                <div className="h-px bg-[#e9e9e9] my-4" />
                
                <div className="space-y-2">
                  <button
                    onClick={handleTimeTrackingClick}
                    className="w-full flex items-center justify-between p-3 rounded-[8px] border border-[#e9e9e9] hover:border-[#0084c7] hover:bg-[#0084c7]/5 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-8 bg-[#f5f5f5] group-hover:bg-[#0084c7]/10 rounded-full flex items-center justify-center transition-colors">
                        <Clock className="size-4 text-[#6b6b6b] group-hover:text-[#0084c7]" />
                      </div>
                      <span className="text-[#1c1c1c] text-[14px] font-medium">Zeiterfassung</span>
                    </div>
                    <ChevronRight className="size-4 text-[#6b6b6b]" />
                  </button>

                  <button
                    onClick={handleRequestsClick}
                    className="w-full flex items-center justify-between p-3 rounded-[8px] border border-[#e9e9e9] hover:border-[#0084c7] hover:bg-[#0084c7]/5 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-8 bg-[#f5f5f5] group-hover:bg-[#0084c7]/10 rounded-full flex items-center justify-center transition-colors">
                        <FileText className="size-4 text-[#6b6b6b] group-hover:text-[#0084c7]" />
                      </div>
                      <span className="text-[#1c1c1c] text-[14px] font-medium">Meine Anträge</span>
                    </div>
                    <ChevronRight className="size-4 text-[#6b6b6b]" />
                  </button>

                  <button
                    onClick={() => {
                      const event = new CustomEvent('navigate', {
                        detail: { page: 'auswertung' }
                      });
                      window.dispatchEvent(event);
                    }}
                    className="w-full flex items-center justify-between p-3 rounded-[8px] border border-[#e9e9e9] hover:border-[#0084c7] hover:bg-[#0084c7]/5 transition-all group"
                  >
                    <div className="flex items-center gap-3">
                      <div className="size-8 bg-[#f5f5f5] group-hover:bg-[#0084c7]/10 rounded-full flex items-center justify-center transition-colors">
                        <TrendingUp className="size-4 text-[#6b6b6b] group-hover:text-[#0084c7]" />
                      </div>
                      <span className="text-[#1c1c1c] text-[14px] font-medium">Auswertungen</span>
                    </div>
                    <ChevronRight className="size-4 text-[#6b6b6b]" />
                  </button>
                </div>
              </CardContent>
            </Card>

            {/* This Week Summary */}
            <Card>
              <CardHeader
                title="Diese Woche"
                subtitle="Übersicht Ihrer Arbeitszeit"
                borderBottom
              />
              <CardContent>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-[#6b6b6b] text-[14px]">Erfasste Stunden</span>
                    <span className="text-[#1c1c1c] text-[20px] font-medium">32.5h</span>
                  </div>
                  <div className="h-px bg-[#e9e9e9]" />
                  <div className="flex justify-between items-center">
                    <span className="text-[#6b6b6b] text-[14px]">Soll-Stunden</span>
                    <span className="text-[#1c1c1c] text-[20px] font-medium">40h</span>
                  </div>
                  <div className="h-px bg-[#e9e9e9]" />
                  <div className="flex justify-between items-center">
                    <span className="text-[#6b6b6b] text-[14px]">Differenz</span>
                    <span className="text-orange-600 text-[20px] font-medium">-7.5h</span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="pt-2">
                    <div className="flex justify-between text-[12px] text-[#6b6b6b] mb-2">
                      <span>Fortschritt</span>
                      <span>81%</span>
                    </div>
                    <div className="h-2 bg-[#e9e9e9] rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#0084c7] rounded-full transition-all"
                        style={{ width: '81%' }}
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Month Overview */}
            <Card>
              <CardHeader
                title="Januar 2026"
                subtitle="Monatliche Übersicht"
                borderBottom
              />
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-2 bg-[#0084c7] rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Gearbeitete Tage</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[15px] font-medium">6</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-2 bg-[#06b6d4] rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Urlaubstage</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[15px] font-medium">0</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-2 bg-[#dc2626] rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Krankheitstage</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[15px] font-medium">1</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <div className="size-2 bg-[#8b5cf6] rounded-full" />
                      <span className="text-[#6b6b6b] text-[13px]">Überstunden-Abbau</span>
                    </div>
                    <span className="text-[#1c1c1c] text-[15px] font-medium">0</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}